import axios from "axios";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Calendar2Week,
  Clock,
  XCircle,
  ChevronLeft,
  ChevronRight,
} from "react-bootstrap-icons";

import LoadingSpinner from "../../components/LoadingSpinner";
import { ApiConnectionString } from "../../constants/ApiConntection";

import "./PublicAvailabilityPage.css";

export default function PublicAvailabilityPage() {
  const { slug } = useParams();

  const [practiceName, setPracticeName] = useState("");
  const [availableDays, setAvailableDays] = useState([]);
  const [actualSessionTime, setActualSessionTime] = useState(0);

  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchAvailability();
  }, [slug]);

  const fetchAvailability = async () => {
    if (!slug) {
      setErr("Practice link is missing.");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setErr("");

      const response = await axios.get(
        `${ApiConnectionString}/api/public/availability/${slug}`
      );

      const data = response.data || {};

      console.log(data);

      const days = data.availableDays || data.AvailableDays || [];

      setPracticeName(
        data.practiceName || data.PracticeName || "Practice"
      );

      setActualSessionTime(
        data.actualSessionTime || data.ActualSessionTime || 0
      );

      setAvailableDays(days);

      if (days.length > 0) {
        const firstDate = getDateOnly(days[0]);
        const parsedDate = parseDateOnly(firstDate);

        setCalendarMonth(parsedDate);
        setSelectedDate(days[0]);
      }
    } catch (error) {
      console.error(error);

      if (error.response?.status === 404) {
        setErr("This practice could not be found.");
      } else {
        setErr("Availability could not be loaded.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getDateOnly = (day) => {
    if (!day) return "";

    return day.date || day.Date || "";
  };

  const getDayDate = (day) => {
    if (!day) return "";

    return day.displayDate || day.DisplayDate || day.date || day.Date || "";
  };

  const getDaySlots = (day) => {
    if (!day) return [];

    return day.slots || day.Slots || [];
  };

  const getSlotStart = (slot) => {
    if (!slot) return "";

    return slot.start || slot.Start || "";
  };

  const getSlotFinish = (slot) => {
    if (!slot) return "";

    return slot.finish || slot.Finish || "";
  };

  const formatTime = (dateValue) => {
    if (!dateValue) return "";

    const match = dateValue.match(/T(\d{2}):(\d{2})/);

    if (!match) return "";

    return `${match[1]}:${match[2]}`;
  };

  const parseDateOnly = (dateString) => {
    if (!dateString) return new Date();

    const parts = dateString.split("-");

    if (parts.length !== 3) {
      return new Date(dateString);
    }

    return new Date(
      Number(parts[0]),
      Number(parts[1]) - 1,
      Number(parts[2])
    );
  };

  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const formatMonthTitle = (date) => {
    return date.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });
  };

  const formatAccessibleDate = (date) => {
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const days = [];

    const startPadding =
      firstDayOfMonth.getDay() === 0
        ? 6
        : firstDayOfMonth.getDay() - 1;

    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }

    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const findAvailableDay = (date) => {
    if (!date) return null;

    const dateKey = formatDateKey(date);

    return availableDays.find(
      (day) => getDateOnly(day) === dateKey
    );
  };

  const isSelectedDate = (date) => {
    if (!date || !selectedDate) return false;

    return formatDateKey(date) === getDateOnly(selectedDate);
  };

  const goToPreviousMonth = () => {
    setCalendarMonth(
      new Date(
        calendarMonth.getFullYear(),
        calendarMonth.getMonth() - 1,
        1
      )
    );
  };

  const goToNextMonth = () => {
    setCalendarMonth(
      new Date(
        calendarMonth.getFullYear(),
        calendarMonth.getMonth() + 1,
        1
      )
    );
  };

  const getAvailabilityRanges = (day) => {
    const slots = getDaySlots(day);

    if (!slots.length) {
      return [];
    }

    const sortedSlots = [...slots].sort(
      (a, b) =>
        new Date(getSlotStart(a)) -
        new Date(getSlotStart(b))
    );

    const ranges = [];

    let rangeStart = getSlotStart(sortedSlots[0]);
    let rangeEnd = getSlotFinish(sortedSlots[0]);
    let previousStart = getSlotStart(sortedSlots[0]);

    for (let i = 1; i < sortedSlots.length; i++) {
      const currentStart = getSlotStart(sortedSlots[i]);
      const currentFinish = getSlotFinish(sortedSlots[i]);

      const previousDate = new Date(previousStart);
      const currentDate = new Date(currentStart);

      const minutesBetween =
        (currentDate.getTime() - previousDate.getTime()) / 60000;

      if (minutesBetween > 15) {
        ranges.push({
          start: rangeStart,
          end: rangeEnd,
        });

        rangeStart = currentStart;
      }

      rangeEnd = currentFinish;
      previousStart = currentStart;
    }

    ranges.push({
      start: rangeStart,
      end: rangeEnd,
    });

    return ranges;
  };

  if (isLoading) {
    return (
      <div className="public-availability-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (err) {
    return (
      <div className="public-availability-page">
        <div className="public-availability-card public-availability-message">
          <div className="public-availability-icon error">
            <XCircle />
          </div>

          <h1>Availability unavailable</h1>
          <p>{err}</p>

          <button
            type="button"
            className="public-availability-btn"
            onClick={fetchAvailability}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const selectedRanges = selectedDate
    ? getAvailabilityRanges(selectedDate)
    : [];

  return (
    <div className="public-availability-page">
      <div className="public-availability-hero">
        <div className="public-availability-hero-icon">
          <Calendar2Week />
        </div>

        <div>
          <span>Public availability</span>
          <h1>{practiceName}</h1>

          <p>
            View general appointment availability. To book or ask about a
            time, please contact the practice directly.
          </p>
        </div>
      </div>

      {availableDays.length === 0 ? (
        <div className="public-availability-card public-availability-message">
          <div className="public-availability-icon">
            <Clock />
          </div>

          <h2>No availability showing</h2>

          <p>
            There are no public appointment times available at the moment.
          </p>
        </div>
      ) : (
        <div className="public-availability-grid">
          <section className="public-availability-card">
            <div className="public-calendar-header">
              <button
                type="button"
                aria-label="Previous month"
                onClick={goToPreviousMonth}
              >
                <ChevronLeft />
              </button>

              <strong>{formatMonthTitle(calendarMonth)}</strong>

              <button
                type="button"
                aria-label="Next month"
                onClick={goToNextMonth}
              >
                <ChevronRight />
              </button>
            </div>

            <div
              className="public-calendar-weekdays"
              aria-hidden="true"
            >
              <span>Mon</span>
              <span>Tue</span>
              <span>Wed</span>
              <span>Thu</span>
              <span>Fri</span>
              <span>Sat</span>
              <span>Sun</span>
            </div>

            <div className="public-calendar-grid">
              {getCalendarDays().map((date, index) => {
                if (!date) {
                  return (
                    <div
                      key={`blank-${index}`}
                      className="public-calendar-day blank"
                    />
                  );
                }

                const availableDay = findAvailableDay(date);
                const isAvailable = Boolean(availableDay);
                const selected = isSelectedDate(date);

                return (
                  <button
                    type="button"
                    key={formatDateKey(date)}
                    className={`public-calendar-day ${
                      isAvailable ? "available" : ""
                    } ${selected ? "selected" : ""}`}
                    disabled={!isAvailable}
                    aria-label={
                      isAvailable
                        ? `${formatAccessibleDate(
                            date
                          )}, availability shown`
                        : `${formatAccessibleDate(
                            date
                          )}, no availability`
                    }
                    onClick={() => setSelectedDate(availableDay)}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>

            <div className="public-calendar-key">
              <div>
                <span className="public-calendar-key-dot available" />
                Available
              </div>

              <div>
                <span className="public-calendar-key-dot unavailable" />
                No availability
              </div>
            </div>
          </section>

          <section className="public-availability-card">
            {selectedDate ? (
              <>
                <div className="public-availability-day-header">
                  <div>
                    <span>Availability</span>

                    <h2>{getDayDate(selectedDate)}</h2>

                    {actualSessionTime > 0 && (
                      <p className="public-availability-session-length">
                        <Clock />
                        Appointment length:{" "}
                        <strong>
                          {actualSessionTime} minutes
                        </strong>
                      </p>
                    )}
                  </div>
                </div>

                <div className="public-availability-ranges">
                  {selectedRanges.map((range, index) => (
                    <div
                      className="public-availability-range"
                      key={`${range.start}-${range.end}-${index}`}
                    >
                      <span>Available between</span>

                      <strong>
                        {formatTime(range.start)} –{" "}
                        {formatTime(range.end)}
                      </strong>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="public-availability-empty-selection">
                <Clock />
                <h2>Choose a date</h2>
                <p>
                  Select a green day on the calendar to view availability.
                </p>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}